import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, Truck, Wrench, Package, Settings, LogOut, User, ChevronDown, Circle } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/fleet", label: "Fleet", icon: Truck, testId: "nav-fleet" },
  { to: "/maintenance", label: "Maintenance", icon: Wrench, testId: "nav-maintenance" },
  { to: "/inventory", label: "Inventory", icon: Package, testId: "nav-inventory" },
];

function SyncIndicator() {
  const [state, setState] = useState({ latest: null });
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/sync/state");
        if (alive) setState(data);
      } catch {}
    };
    tick();
    const id = setInterval(tick, 6000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const latest = state.latest;
  return (
    <div className="flex items-center gap-2 text-xs font-mono" data-testid="sync-indicator">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500/50" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
      </span>
      <span className="text-zinc-400 hidden sm:inline">
        {latest ? (
          <>Updated by <span className="text-zinc-200">{latest.username}</span> · {timeAgo(latest.timestamp)}</>
        ) : (
          <>Live · 6s</>
        )}
      </span>
      <span className="text-zinc-400 sm:hidden">Live</span>
    </div>
  );
}

function timeAgo(iso) {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function Shell({ children }) {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const nav = isAdmin ? [...NAV, { to: "/manage", label: "Manage", icon: Settings, testId: "nav-manage" }] : NAV;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col">
      {/* Top header */}
      <header className="sticky top-0 z-50 bg-zinc-950/95 backdrop-blur-none border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 group" data-testid="brand-link">
            <div className="h-7 w-7 border border-red-500/60 flex items-center justify-center bg-red-500/10">
              <span className="font-mono font-bold text-red-400 text-xs">M</span>
            </div>
            <div className="leading-tight">
              <div className="font-semibold text-sm tracking-tight">MORGAN SHOP</div>
              <div className="text-[10px] font-mono uppercase text-zinc-500 tracking-widest">Tracker · Cloud</div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1 ml-6">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={n.testId}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-sm text-sm font-medium flex items-center gap-2 transition-colors ${
                    isActive
                      ? "bg-zinc-800 text-zinc-50 border-b-2 border-red-500"
                      : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900"
                  }`
                }
              >
                <n.icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-4">
            <SyncIndicator />
            <UserMenu user={user} onLogout={logout} />
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-4 pb-24 md:pb-8">
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-zinc-950 border-t border-zinc-800">
        <div className="grid grid-cols-5">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`bottom-${n.testId}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-2 gap-0.5 text-[10px] font-mono uppercase tracking-wider ${
                  isActive ? "text-red-400 border-t-2 border-red-500" : "text-zinc-500"
                }`
              }
            >
              <n.icon className="h-5 w-5" />
              {n.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

function UserMenu({ user, onLogout }) {
  const initials = (user?.username || "?").slice(0, 2).toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="user-menu-trigger"
        className="flex items-center gap-2 rounded-sm border border-zinc-800 bg-zinc-900 hover:bg-zinc-800 pl-1 pr-2 py-1 transition-colors">
        <div className="h-6 w-6 bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-mono flex items-center justify-center rounded-sm">
          {initials}
        </div>
        <span className="hidden sm:inline text-sm font-medium">{user?.username}</span>
        <ChevronDown className="h-3 w-3 text-zinc-500" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800 text-zinc-100 min-w-[200px]">
        <DropdownMenuLabel className="text-xs text-zinc-500 uppercase font-mono tracking-wider">Signed in as</DropdownMenuLabel>
        <div className="px-2 pb-2">
          <div className="text-sm font-medium">{user?.username}</div>
          <div className="text-xs text-zinc-500">{user?.role}</div>
        </div>
        <DropdownMenuSeparator className="bg-zinc-800" />
        <DropdownMenuItem onClick={onLogout} data-testid="menu-logout" className="cursor-pointer text-red-300 focus:bg-red-950/40 focus:text-red-200">
          <LogOut className="h-4 w-4 mr-2" /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
